import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiSend, FiX, FiMessageCircle, FiMic, FiUpload, FiGlobe, FiChevronDown, FiEdit2, FiPlus, FiClock, FiTrash2, FiMessageSquare, FiUser, FiSearch, FiGrid, FiCalendar, FiMapPin } from 'react-icons/fi';
import api from '../api';


const GIGI_AVATAR = '/gigi-mascot.png';
const C = { charcoal: '#1C1C1E', coral: '#E3425C', coralDark: '#cf4354', coralLight: 'rgba(227,66,92,0.15)' };

// ─── LANGUAGE CONFIG ───
const LANGUAGES = [
  { code: 'en', label: 'English', speechCode: 'en-ZA' },
  { code: 'zu', label: 'isiZulu', speechCode: 'zu-ZA' },
  { code: 'tn', label: 'Setswana', speechCode: 'tn-ZA' },
  { code: 'sep', label: 'Sepitori', speechCode: 'st-ZA' },
  { code: 'st', label: 'Sesotho', speechCode: 'st-ZA' },
  { code: 'xh', label: 'isiXhosa', speechCode: 'xh-ZA' },
  { code: 'af', label: 'Afrikaans', speechCode: 'af-ZA' },
];

const LANGUAGE_GREETINGS = {
  'en': 'Hello! How can I help you today?',
  'zu': 'Sawubona! Ngingakusiza ngani namhlanje?',
  'tn': 'Dumela! Ke ka go thusa ka eng gompieno?',
  'sep': 'Heita! Ke ka go helpa ka eng vandag?',
  'st': 'Dumela! Ke ka o thusa ka eng kajeno?',
  'xh': 'Molo! Ndingakunceda ngantoni namhlanje?',
  'af': 'Hallo! Hoe kan ek jou vandag help?',
};

// ─── SMART CONTEXT PROMPTS ───
const CONTEXT_PROMPTS = {
  'dashboard': [
    { label: 'Create new event', msg: 'Help me create a new event' },
    { label: 'View my events', msg: 'Show me my events overview' },
    { label: 'Explore vendors', msg: 'Help me find vendors for my event' },
    { label: 'Help me plan', msg: 'I need help planning my event step by step' },
  ],
  'overview': [
    { label: 'Set budget', msg: 'Help me set up my event budget' },
    { label: 'Add guests', msg: 'Help me manage my guest list' },
    { label: 'Choose theme', msg: 'Suggest some themes for my event' },
    { label: 'Write description', msg: 'generate_description' },
  ],
  'budget': [
    { label: 'Suggest budget split', msg: 'Suggest a budget breakdown for my event' },
    { label: 'Add expense', msg: 'Help me add a new expense to my budget' },
    { label: 'Budget tips', msg: 'Give me tips on managing my event budget effectively' },
  ],
  'guests': [
    { label: 'Import guests', msg: 'How can I import my guest list?' },
    { label: 'Send invites', msg: 'Help me send invitations to my guests' },
    { label: 'RSVP tips', msg: 'Tips for managing RSVPs and getting responses' },
  ],
  'checklist': [
    { label: 'Generate checklist', msg: 'generate_checklist' },
    { label: 'Add task', msg: 'Help me add a new task to my checklist' },
    { label: 'Progress update', msg: 'How am I doing with my event planning progress?' },
  ],
  'timeline': [
    { label: 'Suggest programme', msg: 'suggest_timeline' },
    { label: 'Add time slot', msg: 'Help me add a new time slot to my programme' },
    { label: 'Day-of tips', msg: 'Give me tips for managing the day-of schedule' },
  ],
  'invitations': [
    { label: 'Create invitation', msg: 'Help me create a beautiful invitation' },
    { label: 'Share link', msg: 'How do I share my event invitation link?' },
    { label: 'RSVP tracking', msg: 'Help me track RSVPs for my event' },
    { label: '💝 Write thank you', msg: 'generate_thankyou' },
  ],
  'gallery': [
    { label: 'Upload photos', msg: 'How do I upload photos to my event gallery?' },
    { label: 'Gallery tips', msg: 'Tips for organizing my event gallery' },
  ],
  'speakers': [
    { label: 'Add speaker', msg: 'Help me add a speaker to my event' },
    { label: 'MC tips', msg: 'Tips for choosing and briefing an MC' },
  ],
  'registry': [
    { label: 'Set up registry', msg: 'Help me set up a gift registry' },
    { label: 'Gift ideas', msg: 'Suggest gift ideas for my event registry' },
  ],
  'vision': [
    { label: 'Décor ideas', msg: 'Give me décor and setup ideas for my event' },
    { label: 'Colour palette', msg: 'Suggest a colour palette for my event' },
    { label: 'Venue styling', msg: 'How should I style and decorate my venue?' },
    { label: 'Theme ideas', msg: 'Suggest creative themes for my event' },
    { label: 'DIY tips', msg: 'Give me budget-friendly DIY decoration ideas' },
  ],
  'vendor': [
    { label: 'Find events', msg: 'Show me events near me that need my services' },
    { label: 'Pricing help', msg: 'How should I price my services competitively in South Africa?' },
    { label: 'Reply to client', msg: 'Give me professional reply templates for client inquiries' },
    { label: 'Grow business', msg: 'What can I do to get more bookings on Grand Gather?' },
    { label: 'Send rate card', msg: 'I want to send my rate card to a potential client' },
  ],
  'kgatso': [
    { label: 'Plan a funeral', msg: 'Help me plan a dignified funeral service step by step' },
    { label: 'Plan an unveiling', msg: 'Help me plan a memorial unveiling ceremony' },
    { label: 'Burial checklist', msg: 'What do I need to prepare for a burial? Give me the full checklist' },
    { label: 'Funeral costs', msg: 'What are the typical costs involved in planning a funeral in South Africa?' },
  ],
};

// ─── EVENT DETECTION KEYWORDS ───
const EVENT_KEYWORDS = ['hosting', 'planning', 'organize', 'organise', 'throwing', 'celebrate', 'celebration', 'lobola', 'wedding', 'birthday', 'funeral', 'memorial', 'graduation', 'matric dance', 'engagement', 'gender reveal', 'baby shower', 'corporate event', 'conference', 'party', 'umembeso'];
const GUEST_PATTERN = /(\d+)\s*(guests?|people|attendees?|pax)/i;
const BUDGET_PATTERN = /(?:budget|spend|R)\s*(?:is\s*)?(?:R?\s*)(\d[\d,.\s]*)/i;
const LOCATION_PATTERN = /(?:in|at|venue[:\s]*)\s+([A-Z][a-zA-Z\s,]+?)(?:\.|,|$|\s+(?:with|for|and|budget|guests?))/i;
const DATE_PATTERN = /(?:on|date[:\s]*)\s+(\d{1,2}(?:st|nd|rd|th)?\s+\w+(?:\s+\d{4})?|\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})/i;

function parseEventFromMessage(text) {
  if (!text || text.length < 20) return null;
  const lower = text.toLowerCase();
  const hasKeyword = EVENT_KEYWORDS.some(kw => lower.includes(kw));
  if (!hasKeyword) return null;

  const parsed = {};
  let detectedType = null;
  if (lower.includes('wedding')) detectedType = 'Wedding';
  else if (lower.includes('lobola')) detectedType = 'Lobola';
  else if (lower.includes('umembeso')) detectedType = 'Umembeso';
  else if (lower.includes('birthday')) detectedType = 'Birthday';
  else if (lower.includes('funeral')) detectedType = 'Funeral';
  else if (lower.includes('memorial')) detectedType = 'Memorial / Unveiling';
  else if (lower.includes('graduation')) detectedType = 'Graduation';
  else if (lower.includes('matric dance')) detectedType = 'Matric Dance';
  else if (lower.includes('engagement')) detectedType = 'Engagement';
  else if (lower.includes('gender reveal')) detectedType = 'Gender Reveal';
  else if (lower.includes('corporate') || lower.includes('conference')) detectedType = 'Corporate Event';
  else if (lower.includes('party') && lower.includes('kid')) detectedType = 'Kids Party';

  if (detectedType) parsed.event_type = detectedType;

  const guestMatch = text.match(GUEST_PATTERN);
  if (guestMatch) parsed.estimated_guests = parseInt(guestMatch[1], 10);

  const budgetMatch = text.match(BUDGET_PATTERN);
  if (budgetMatch) parsed.budget = parseFloat(budgetMatch[1].replace(/[\s,]/g, ''));

  const locationMatch = text.match(LOCATION_PATTERN);
  if (locationMatch) parsed.location = locationMatch[1].trim();

  const dateMatch = text.match(DATE_PATTERN);
  if (dateMatch) parsed.event_date = dateMatch[1].trim();

  if (Object.keys(parsed).length >= 1) return parsed;
  return null;
}

// ─── VENDOR MODE CONTENT ───
const VENDOR_GREETINGS = {
  'new': "Welcome to Grand Gather! I'm GiGi, your business growth coach. I can check which hosts are actively planning events that match your services and help you connect with them. What would you like help with?",
  'returning': "Welcome back! I'm GiGi, ready to help you grow. Want me to check for active hosts needing your services? I can also help with pricing, client replies, and more.",
  'default': "Hi there! I'm GiGi, your Grand Gather business coach. I can check which hosts are actively planning events that match your services, help you connect with them, guide your pricing, and grow your bookings. How can I help?"
};

function getVendorProactiveSuggestions(vendorContext) {
  const suggestions = [];
  if (vendorContext?.unreadChats > 0) {
    suggestions.push({ text: `You have ${vendorContext.unreadChats} unread message${vendorContext.unreadChats > 1 ? 's' : ''} from clients! Quick responses build trust and lead to more bookings. Tap to check your chats.`, action: 'vendor_reply_reminder' });
  }
  if (vendorContext?.pendingBookings > 0) {
    suggestions.push({ text: `You have ${vendorContext.pendingBookings} pending booking${vendorContext.pendingBookings > 1 ? 's' : ''} waiting for your response. Responding within 24 hours increases your booking rate by 80%!`, action: 'vendor_booking_reminder' });
  }
  if (vendorContext?.portfolioCount === 0) {
    suggestions.push({ text: "Your portfolio is empty! Vendors with at least 5 photos get 3x more inquiries. Upload some of your best work to attract clients.", action: 'vendor_portfolio_tip' });
  } else if (vendorContext?.portfolioCount < 5) {
    suggestions.push({ text: `You have ${vendorContext.portfolioCount} portfolio image${vendorContext.portfolioCount > 1 ? 's' : ''}. Vendors with 5+ images get significantly more inquiries. Add a few more!`, action: 'vendor_portfolio_tip' });
  }
  if (vendorContext?.pendingBookings === 0 && (vendorContext?.totalBookings || 0) < 5) {
    suggestions.push({ text: "Looking for new clients? I can check which hosts are actively planning events that need your services. Want me to look for matches now?", action: 'vendor_find_events' });
  }
  if (!vendorContext?.hasBanking) {
    suggestions.push({ text: "Don't forget to add your banking details so you can receive payments from clients!", action: 'vendor_banking_tip' });
  }
  if (vendorContext?.walletBalance > 0) {
    suggestions.push({ text: `You have R${vendorContext.walletBalance.toLocaleString()} in your wallet. You can withdraw to your bank account anytime!`, action: 'vendor_wallet_tip' });
  }
  // Always have at least one fallback bubble
  if (suggestions.length === 0) {
    suggestions.push({ text: 'Tap to chat with GiGi! I\'m here to help grow your business.', action: 'vendor_general_help' });
  }
  return suggestions;
}

function getVendorWelcomeBubble(userName, vendorContext, tab) {
  const name = userName ? userName.split(' ')[0] : '';
  const tabMessages = {
    'overview': vendorContext?.unreadChats > 0
      ? `${name}, you have ${vendorContext.unreadChats} unread message${vendorContext.unreadChats > 1 ? 's' : ''}! Quick replies keep clients happy.`
      : `${name}, looking good! Keep your profile updated and portfolio fresh to attract more clients.`,
    'bookings': vendorContext?.pendingBookings > 0
      ? `${name}, don't keep clients waiting! Respond to pending bookings promptly.`
      : `${name}, your bookings are all up to date. Great work!`,
    'chats': `${name}, remember — professional and prompt communication is key! I'll filter out any contact info to keep things secure.`,
    'portfolio': `${name}, your portfolio is your first impression! Show your absolute best work here.`,
    'wallet': `${name}, here's your financial overview. Need tips on pricing your services?`,
    'banking': `${name}, make sure your banking details are correct so withdrawals go smoothly.`,
    'profile': `${name}, keep your profile updated! Accurate info helps clients find you.`,
    'notifications': `${name}, staying on top of notifications keeps you ahead of the game!`,
  };
  return tabMessages[tab] || `${name}, I'm here to help you grow your business!`;
}

const VENDOR_QUICK_CHIPS = [
  { label: 'Check active hosts', msg: 'Show me active hosts planning events that need my services' },
  { label: 'Pricing help', msg: 'How should I price my services competitively in South Africa?' },
  { label: 'Reply templates', msg: 'Give me professional reply templates for client messages' },
  { label: 'Get more bookings', msg: 'How do I get more client bookings on Grand Gather?' },
];

// ─── EVENT MODE CONTENT ───
const GREETINGS = {
  'Wedding': "Congratulations on your upcoming wedding! I'm GiGi, and I'm here to make sure every detail is perfect for your special day. What can I help you with?",
  'Lobola': "What a beautiful cultural milestone! I'm GiGi, here to help you plan a meaningful Lobola celebration that honours tradition. How can I assist?",
  'Umembeso': "How exciting! Umembeso is such a special occasion. I'm GiGi, ready to help you prepare meaningful gifts and a beautiful ceremony. What do you need?",
  'Birthday': "Happy planning! I'm GiGi, your celebration assistant. Let's make this birthday unforgettable! What would you like help with?",
  'Kids Party': "How fun! I'm GiGi, and I love helping plan kids' parties. Let's create magical memories! What's on your mind?",
  'Graduation': "What an achievement to celebrate! I'm GiGi, here to help you plan a graduation celebration that matches this incredible milestone!",
  'Gender Reveal': "How exciting! I'm GiGi, ready to help plan the perfect reveal moment. Let's make it memorable!",
  'Engagement': "Love is in the air! I'm GiGi, here to help plan a beautiful engagement celebration. What can I help with?",
  'Memorial / Unveiling': "I understand this is a meaningful time of remembrance. I'm GiGi, here to help you plan a dignified and beautiful memorial. Please let me know how I can support you.",
  'Funeral': "I'm so sorry for your loss. I'm GiGi, and I'm here to gently help you with the arrangements so you can focus on what matters most - being with your loved ones. Please take your time, and let me know how I can help.",
  'Matric Dance': "Matric Dance! How exciting! I'm GiGi, ready to help make this night absolutely unforgettable. What do you need?",
  'Corporate Event': "Welcome! I'm GiGi, your event planning assistant. Let's make this corporate event professional and engaging. How can I help?",
  'default': "Hello! I'm GiGi, your Grand Gather planning assistant. I'm here to help with tips, inspiration, and making your event planning easier. What can I help you with?"
};

function getProactiveSuggestions(event, context) {
  const suggestions = [];
  const type = event?.event_type || '';
  const isSomber = ['Funeral', 'Memorial / Unveiling'].includes(type);

  // Dashboard-level suggestions (no specific event needed)
  if (context?.page === 'dashboard') {
    if (context?.eventCount === 0) {
      suggestions.push({ text: "Welcome! Tap to chat with GiGi — I'll help you create your first event from start to finish.", action: 'create_event' });
    } else {
      suggestions.push({ text: 'Tap to chat with GiGi! I can help with vendors, invitations, budgets, and more.', action: 'dashboard_help' });
    }
  }

  if (!event) {
    // Fallback for pages without a specific event
    if (suggestions.length === 0) {
      suggestions.push({ text: 'Tap to chat with GiGi! Ask me anything about planning your event.', action: 'general_help' });
    }
    return suggestions;
  }

  if (!event.location) {
    suggestions.push({ text: isSomber ? 'I notice no venue has been set yet. Would you like help choosing an appropriate venue?' : "I see you haven't set a location yet. Would you like help choosing the perfect venue?", action: 'suggest_venue' });
  }
  if (!event.event_date) {
    suggestions.push({ text: "You haven't set a date yet. Would you like me to help you pick a good date?", action: 'suggest_date' });
  }
  if (!event.description) {
    suggestions.push({ text: `Would you like me to write a ${isSomber ? 'thoughtful' : 'beautiful'} description for your ${type || 'event'}?`, action: 'generate_description' });
  }
  if (context?.tab === 'guests' && context?.guestCount === 0) {
    suggestions.push({ text: "Your guest list is empty. Would you like tips on managing your guest list?", action: 'guest_tips' });
  }
  if (context?.tab === 'budget' && context?.budgetItems === 0) {
    suggestions.push({ text: `I can suggest budget categories for a ${type}. Want me to set those up for you?`, action: 'suggest_budget' });
  }
  if (context?.tab === 'checklist' && context?.checklistItems === 0) {
    suggestions.push({ text: `I've got a checklist ready for ${type || 'your event'}. Shall I generate it for you?`, action: 'generate_checklist' });
  }
  if (context?.tab === 'timeline' && context?.timelineItems === 0) {
    suggestions.push({ text: `Would you like me to suggest a day programme for a ${type || 'celebration'}?`, action: 'suggest_timeline' });
  }
  if (context?.tab === 'invitations' && context?.invitationCount === 0) {
    suggestions.push({ text: "Ready to invite your guests? Let me help you create a beautiful invitation!", action: 'invite_tips' });
  }
  if (context?.tab === 'speakers' && context?.speakerCount === 0) {
    suggestions.push({ text: isSomber ? "Would you like to add speakers or tribute presenters for the ceremony?" : "Would you like to add speakers or MCs for the event?", action: 'speaker_tips' });
  }
  if (context?.tab === 'registry' && !isSomber && context?.registryItems === 0) {
    suggestions.push({ text: "Would you like to set up a gift registry or cash contribution fund?", action: 'registry_tips' });
  }

  // Always have at least one fallback bubble
  if (suggestions.length === 0) {
    suggestions.push({ text: 'Tap to chat with GiGi! I\'m here to help with anything you need.', action: 'general_help' });
  }
  return suggestions;
}

function getWelcomeBubble(userName, event, tab) {
  const name = userName ? userName.split(' ')[0] : '';
  const type = event?.event_type || '';
  const isSomber = ['Funeral', 'Memorial / Unveiling'].includes(type);
  const title = event?.title || 'your event';

  const tabMessages = {
    'overview': isSomber
      ? `${name}, I'm here to help you through the planning. Take it one step at a time.`
      : `Hey ${name}! Looking great so far! Let's check how "${title}" is shaping up.`,
    'timeline': isSomber
      ? `${name}, let's set up the order of service. I can suggest a programme if you'd like.`
      : `${name}, let's plan the flow of the day! A great programme keeps the energy going.`,
    'budget': isSomber
      ? `${name}, I can help organise costs so nothing is missed. Want me to set up categories?`
      : `${name}, let's make sure every rand counts! I can suggest budget splits for you.`,
    'guests': isSomber
      ? `${name}, let me help you manage the guest list. I know there's a lot to coordinate.`
      : `${name}! The more the merrier! Let's get your guest list sorted.`,
    'invitations': isSomber
      ? `${name}, I can help you create a respectful and dignified invitation.`
      : `${name}, time to spread the word! Let's create a stunning invitation.`,
    'registry': `${name}, let's set up something special for contributions and gifts!`,
    'gallery': `${name}, this is where all the beautiful memories will live!`,
    'wall': isSomber
      ? `${name}, this space is for messages of comfort and support.`
      : `${name}, the event wall is buzzing! Let's keep the conversation going.`,
    'speakers': isSomber
      ? `${name}, let's add the speakers and tribute presenters.`
      : `${name}, every great event needs great speakers! Let's add yours.`,
    'delegation': isSomber
      ? `${name}, it helps to share the load. Let's assign roles to your support team.`
      : `${name}, teamwork makes the dream work! Let's delegate some tasks.`,
    'checklist': isSomber
      ? `${name}, one step at a time. I can generate a checklist to guide you.`
      : `${name}, let's crush this checklist! I can auto-generate tasks for you.`,
    'corporate': `${name}, let's set up polls and Q&A to keep your audience engaged!`,
  };

  if (tab === 'dashboard') {
    if (!event) return `Hey ${name || 'there'}! Welcome to Grand Gather! Ready to plan something amazing? Create your first event to get started!`;
    return isSomber
      ? `Welcome back, ${name}. I'm here whenever you need me.`
      : `Hey ${name}! Welcome back to Grand Gather! You've got ${event ? 'events cooking' : 'a blank canvas'}. Let's make magic happen!`;
  }

  return tabMessages[tab] || (isSomber
    ? `${name}, I'm right here if you need anything.`
    : `${name}, you're doing amazing! Need any help here?`);
}

// ─── GET SMART PROMPTS BASED ON CONTEXT ───
function getSmartPrompts(mode, context, event) {
  if (mode === 'vendor') return CONTEXT_PROMPTS['vendor'] || [];
  if (mode === 'kgatso' || context?.page === 'kgatso') return CONTEXT_PROMPTS['kgatso'] || [];
  const tab = context?.tab || context?.page || '';
  if (tab === 'dashboard') return CONTEXT_PROMPTS['dashboard'] || [];
  if (CONTEXT_PROMPTS[tab]) return CONTEXT_PROMPTS[tab];
  if (event) {
    return [
      { label: 'Tips for my event', msg: `Give me tips for planning a ${event.event_type}` },
      { label: 'Budget help', msg: 'How should I plan my budget?' },
      { label: 'Inspire me', msg: `Give me creative ideas and inspiration for my ${event.event_type}` },
    ];
  }
  return CONTEXT_PROMPTS['dashboard'] || [];
}

export default function GigiBot({ event, context, onAction, userName, mode = 'event', vendorContext, displayMode = 'bubble', onClose }) {
  const [open, setOpen] = useState(false);
  // expanded state removed — panel always uses full size
  const [messages, setMessages] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [chatSessions, setChatSessions] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(() => {
    try { return sessionStorage.getItem('gigi_greeted') === 'true'; } catch { return false; }
  });
  const [proactiveShown, setProactiveShown] = useState(new Set());
  const [bubble, setBubble] = useState(null);
  const [gigiBadge, setGigiBadge] = useState(() => {
    try { return JSON.parse(localStorage.getItem('gigi_unread') || '[]').length; } catch { return 0; }
  });
  useEffect(() => {
    const update = () => { try { const s = JSON.parse(localStorage.getItem('gigi_unread')||'[]'); setGigiBadge(s.length); } catch { setGigiBadge(0); } };
    window.addEventListener('gigi-unread-update', update);
    return () => window.removeEventListener('gigi-unread-update', update);
  }, []);
  const [userDismissed, setUserDismissed] = useState(() => {
    try {
      const pageKey = context?.tab || context?.page || 'global';
      return sessionStorage.getItem('gigi_dismissed_' + pageKey) === 'true';
    } catch { return false; }
  });
  const userDismissedRef = useRef(userDismissed);
  useEffect(() => { userDismissedRef.current = userDismissed; }, [userDismissed]);
  const [contextReminder, setContextReminder] = useState(null); // { hasContext, sessionId, lastMessage, messageCount, lastActive }

  // Language state
  const [language, setLanguage] = useState(() => {
    try {
      return localStorage.getItem('gigi_language') || 'en';
    } catch { return 'en'; }
  });
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);

  // Mobile detection — adjust bottom position to clear tab bar
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Voice input state
  const [isRecording, setIsRecording] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const recognitionRef = useRef(null);

  // File upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Event detection card state
  const [parsedEventCard, setParsedEventCard] = useState(null);
  const [editingParsedEvent, setEditingParsedEvent] = useState(false);

  // Context memory state
  const [contextSummary, setContextSummary] = useState(null);
  const [editableParsedEvent, setEditableParsedEvent] = useState(null);

  const chatEndRef = useRef(null);
  const bubbleTimerRef = useRef(null);
  const langDropdownRef = useRef(null);

  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

  // Check voice support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setVoiceSupported(!!SpeechRecognition);
  }, []);

  // Close language dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load chat history from database
  useEffect(() => {
    if (historyLoaded) return;
    const eventId = event?.id;
    if (!eventId) { setHistoryLoaded(true); return; }
    (async () => {
      try {
        // Load current/latest session
        const res = await api.get(`/ai/chat-history/${eventId}`);
        if (res.data && res.data.length > 0) {
          setMessages(res.data.map(m => ({ role: m.role, text: m.text })));
          setHasGreeted(true);
        }
        // Load sessions list
        const sessRes = await api.get(`/ai/chat-sessions/${eventId}`);
        if (sessRes.data) setChatSessions(sessRes.data);
        // Set current session_id from latest message
        if (sessRes.data && sessRes.data.length > 0) {
          setSessionId(sessRes.data[0].session_id);
        }
      } catch (e) {}
      setHistoryLoaded(true);
    })();
  }, [event?.id, historyLoaded]);

  // Fetch context reminder when GiGi opens (previous session summary)
  useEffect(() => {
    if (open && event?.id && !contextReminder && historyLoaded) {
      api.get(`/ai/chat-context/${event.id}`).then(res => {
        if (res.data?.hasContext) {
          setContextReminder(res.data);
          setContextSummary(res.data.contextSummary || null);
        }
      }).catch(() => {});
    }
  }, [open, event?.id, historyLoaded, contextReminder]);

  // Save language to localStorage
  useEffect(() => {
    try { localStorage.setItem('gigi_language', language); } catch {}
  }, [language]);

  // Initial greeting (only if no history was loaded)
  useEffect(() => {
    if (!hasGreeted && historyLoaded) {
      if (mode === 'kgatso') {
        const name = userName ? userName.split(' ')[0] : '';
        const kgatsoGreeting = name
          ? `${name}, I'm GiGi. I'm here to help you through funeral and memorial planning with Kgatso Funerals. Whether you need help with burial preparations, understanding costs, or planning an unveiling ceremony — I'll guide you every step of the way. How can I help?`
          : "I'm GiGi, here to help you through funeral and memorial planning with Kgatso Funerals. Whether you need help with burial preparations, understanding costs, or planning an unveiling — I'll guide you every step of the way. How can I help?";
        setMessages([{ role: 'gigi', text: kgatsoGreeting }]);
        setHasGreeted(true);
      } else if (mode === 'vendor') {
        const name = userName ? userName.split(' ')[0] : '';
        let baseGreeting = vendorContext?.bookings > 0 ? VENDOR_GREETINGS['returning'] : VENDOR_GREETINGS['new'];
        // Inject context memory if available
        if (contextSummary) {
          baseGreeting = `Welcome back! ${contextSummary}\n\nWould you like to pick up where we left off?`;
        }
        const personalGreeting = name ? baseGreeting.replace("I'm GiGi", `${name}! I'm GiGi`) : baseGreeting;
        setMessages([{ role: 'gigi', text: personalGreeting }]);
        setHasGreeted(true);
      } else if (event) {
        const type = event.event_type || 'default';
        const name = userName ? userName.split(' ')[0] : '';
        let baseGreeting = GREETINGS[type] || GREETINGS['default'];
        // Inject context memory if available
        if (contextSummary) {
          baseGreeting = `Welcome back, ${name || ''}! ${contextSummary}\n\nWould you like to continue where we left off?`;
        }
        const personalGreeting = name && !contextSummary
          ? baseGreeting.replace("I'm GiGi", `${name}! I'm GiGi`)
          : baseGreeting;
        setMessages([{ role: 'gigi', text: personalGreeting }]);
        setHasGreeted(true);
      }
    }
  }, [event, hasGreeted, historyLoaded, userName, mode, vendorContext, contextSummary]);

  // Proactive suggestions — show contextual bubble with "Tap to chat with GiGi"
  useEffect(() => {
    if (open) return;

    let suggestions = [];
    if (mode === 'vendor') {
      suggestions = getVendorProactiveSuggestions(vendorContext);
    } else {
      suggestions = getProactiveSuggestions(event, context);
    }
    const unshown = suggestions.filter(s => !proactiveShown.has(s.action));

    // Only show bubble — no sounds, no badges, no toasts
    if (!userDismissed && unshown.length > 0 && !bubble) {
      bubbleTimerRef.current = setTimeout(() => {
        setBubble(unshown[0]);
      }, 5000);
    }
    return () => {
      if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
    };
  }, [event, context, open, proactiveShown, bubble, mode, vendorContext, userDismissed]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const dismissBubble = () => {
    if (bubble) {
      setProactiveShown(prev => new Set([...prev, bubble.action]));
      // Store as unread suggestion — will show when GiGi opens
      try {
        const stored = JSON.parse(localStorage.getItem('gigi_unread') || '[]');
        stored.push({ text: bubble.text, action: bubble.action, ts: Date.now() });
        localStorage.setItem('gigi_unread', JSON.stringify(stored));
        window.dispatchEvent(new CustomEvent('gigi-unread-update'));
      } catch {}
      setBubble(null);
      // Permanently dismiss — no more pop-ups on this page
      setUserDismissed(true);
      const pageKey = context?.tab || context?.page || 'global';
      try { sessionStorage.setItem('gigi_dismissed_' + pageKey, 'true'); } catch {}
    }
  };

  const handleBubbleClick = () => {
    if (bubble) {
      setOpen(true);
      setUserDismissed(false);
      setMessages(prev => [...prev, { role: 'gigi', text: bubble.text }]);
      setProactiveShown(prev => new Set([...prev, bubble.action]));
      setBubble(null);
      handleAction(bubble.action);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setExpanded(false);
    setUserDismissed(true);
    setBubble(null);
    setLangDropdownOpen(false);
    const pageKey = context?.tab || context?.page || 'global';
    sessionStorage.setItem('gigi_dismissed_' + pageKey, 'true');
    onClose?.();
  };

  const handleOpen = () => {
    setOpen(true);
    setUserDismissed(false);
    setBubble(null);
    const pageKey = context?.tab || context?.page || 'global';
    sessionStorage.removeItem('gigi_dismissed_' + pageKey);
    // Load unread suggestions as messages
    try {
      const stored = JSON.parse(localStorage.getItem('gigi_unread') || '[]');
      if (stored.length > 0) {
        stored.forEach(s => {
          setMessages(prev => [...prev, { role: 'gigi', text: s.text }]);
        });
        localStorage.setItem('gigi_unread', '[]');
        window.dispatchEvent(new CustomEvent('gigi-unread-update'));
      }
    } catch {}
  };

  // Language change handler
  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    setLangDropdownOpen(false);
    const greeting = LANGUAGE_GREETINGS[langCode] || LANGUAGE_GREETINGS['en'];
    setMessages(prev => [...prev, { role: 'gigi', text: greeting }]);
  };

  // Voice input handlers
  const startRecording = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = currentLang.speechCode;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.onresult = (e) => {
      let transcript = '';
      for (let i = 0; i < e.results.length; i++) {
        transcript += e.results[i][0].transcript;
      }
      setInput(transcript);
    };
    recognition.onend = () => {
      setIsRecording(false);
      setInput(prev => {
        if (prev.trim()) {
          setTimeout(() => {
            sendMessageFromVoice(prev.trim());
          }, 100);
        }
        return prev;
      });
    };
    recognition.onerror = () => {
      setIsRecording(false);
    };
    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };

  const sendMessageFromVoice = async (msg) => {
    if (!msg || sending) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);

    const parsed = parseEventFromMessage(msg);
    if (parsed && parsed.event_type) {
      setParsedEventCard(parsed);
      setEditableParsedEvent({ ...parsed });
    }

    setSending(true);
    try {
      const payload = mode === 'vendor'
        ? { message: msg, language, event_type: 'vendor_assistant', event_context: buildVendorContext() }
        : { message: msg, language, event_type: event?.event_type, event_context: buildEventContext() };
      const res = await api.post('/ai/chat', payload);
      setMessages(prev => [...prev, { role: 'gigi', text: res.data.response, quickChips: res.data.quickChips || null, outreachEvents: res.data.outreach_events || null }]);
      if (res.data.fillData && onAction) onAction(res.data.fillType, res.data.fillData);
      if (res.data.autoActions && onAction) {
        const actionType = res.data.autoActions[0]?.type;
        const items = res.data.autoActions.map(a => a.data);
        if (actionType === 'add_budget') {
          await onAction('add_budget_batch', items);
          onAction('show_toast', { message: `GiGi set up ${items.length} budget categories for you! Check your Budget tab.`, tab: 'budget' });
        } else if (actionType === 'add_checklist') {
          await onAction('add_checklist_batch', items);
          onAction('show_toast', { message: `GiGi set up ${items.length} checklist items for you!`, tab: 'checklist' });
        } else if (actionType === 'add_timeline') {
          await onAction('add_timeline_batch', items);
          onAction('show_toast', { message: `GiGi set up your day programme with ${items.length} time slots!`, tab: 'timeline' });
        } else if (actionType === 'remove_timeline') {
          await onAction('remove_timeline', res.data.autoActions[0].data);
          onAction('show_toast', { message: 'GiGi updated your programme!', tab: 'timeline' });
        } else if (actionType === 'update_timeline') {
          await onAction('update_timeline', res.data.autoActions[0].data);
          onAction('show_toast', { message: 'GiGi updated your programme!', tab: 'timeline' });
        } else if (actionType === 'reload_event') {
          if (onAction) onAction('reload_event');
        } else {
          for (const a of res.data.autoActions) { await onAction(a.type, a.data); }
        }
      }
    } catch {
      setMessages(prev => [...prev, { role: 'gigi', text: "I'm having a moment! Could you try asking that again?" }]);
    }
    setSending(false);
  };

  // File upload handlers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setUploadPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setUploadPreview(null);
    }
  };

  const clearUpload = () => {
    setUploadedFile(null);
    setUploadPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Build event context with sessionId and context memory
  const buildEventContext = (extra = {}) => ({
    ...context,
    eventId: event?.id,
    sessionId,
    title: event?.title,
    location: event?.location,
    event_date: event?.event_date,
    event_time: event?.event_time,
    end_time: event?.end_time,
    budget: event?.budget,
    guests: event?.estimated_guests,
    previousContext: contextSummary || null,
    ...extra,
  });

  const buildVendorContext = () => ({
    mode: 'vendor',
    sessionId,
    business_name: vendorContext?.businessName,
    business_category: vendorContext?.businessCategory,
    business_location: vendorContext?.businessLocation,
    price_range: vendorContext?.priceRange,
    pending_bookings: vendorContext?.pendingBookings,
    total_bookings: vendorContext?.bookings,
    portfolio_count: vendorContext?.portfolioCount,
    wallet_balance: vendorContext?.walletBalance,
    active_chats: vendorContext?.chatCount,
    unread_messages: vendorContext?.unreadChats,
  });

  // Start a new chat session
  const handleNewChat = async () => {
    if (!event?.id) return;
    try {
      const res = await api.post(`/ai/new-chat/${event.id}`);
      setSessionId(res.data.sessionId);
      setMessages([]);
      setHasGreeted(false);
      setHistoryLoaded(true); // Don't reload from DB
      setShowHistory(false);
      // Refresh sessions list
      const sessRes = await api.get(`/ai/chat-sessions/${event.id}`);
      if (sessRes.data) setChatSessions(sessRes.data);
    } catch (e) {}
  };

  // Load a specific chat session
  const handleLoadSession = async (sid) => {
    if (!event?.id) return;
    try {
      const res = await api.get(`/ai/chat-session/${event.id}/${sid || 'null'}`);
      if (res.data && res.data.length > 0) {
        const loadedMsgs = res.data.map(m => ({ role: m.role, text: m.text }));
        // If we have context summary, add a reminder message at the top
        if (contextSummary) {
          loadedMsgs.unshift({ role: 'gigi', text: `📝 *Picking up where we left off...*\n\n${contextSummary}\n\nFeel free to ask me anything or continue from here!` });
        }
        setMessages(loadedMsgs);
        setSessionId(sid);
        setHasGreeted(true);
      }
      setShowHistory(false);
      setContextReminder(null);
    } catch (e) {}
  };

  // Delete a chat session
  const handleDeleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!event?.id) return;
    try {
      await api.delete(`/api/ai/chat-session/${event.id}/${sid || 'null'}`);
      // If we deleted the current session, start fresh
      if (sid === sessionId) {
        setMessages([]);
        setHasGreeted(false);
        setSessionId(null);
      }
      // Refresh sessions list
      const sessRes = await api.get(`/ai/chat-sessions/${event.id}`);
      if (sessRes.data) setChatSessions(sessRes.data);
    } catch (e) {}
  };

  const handleAction = async (action) => {
    const type = event?.event_type || '';
    const isSomber = ['Funeral', 'Memorial / Unveiling'].includes(type);

    if (action === 'generate_description') {
      setMessages(prev => [...prev, { role: 'user', text: 'Yes, please generate a description!' }]);
      setSending(true);
      try {
        const res = await api.post('/ai/chat', {
          message: `generate_description`,
          language: language,
          event_type: type,
          event_context: buildEventContext()
        });
        setMessages(prev => [...prev, { role: 'gigi', text: res.data.response, quickChips: res.data.quickChips || null, outreachEvents: res.data.outreach_events || null }]);
        if (res.data.fillData && onAction) onAction('fill_description', res.data.fillData);
      } catch {
        setMessages(prev => [...prev, { role: 'gigi', text: "I had trouble generating that. Let me try again later!" }]);
      }
      setSending(false);
    } else if (action === 'suggest_budget' || action === 'suggest_budget_categories') {
      setMessages(prev => [...prev, { role: 'user', text: 'Yes, suggest budget categories!' }]);
      setSending(true);
      try {
        const res = await api.post('/ai/chat', {
          message: 'suggest_budget_categories', language, event_type: type,
          event_context: buildEventContext()
        });
        setMessages(prev => [...prev, { role: 'gigi', text: res.data.response, quickChips: res.data.quickChips || null, outreachEvents: res.data.outreach_events || null }]);
        if (res.data.autoActions && onAction) {
          await onAction('add_budget_batch', res.data.autoActions.map(a => a.data));
          onAction('show_toast', { message: `GiGi set up ${res.data.autoActions.length} budget categories for you! Check your Budget tab.`, tab: 'budget' });
        }
      } catch {
        setMessages(prev => [...prev, { role: 'gigi', text: "I couldn't set that up right now. Please try again!" }]);
      }
      setSending(false);
    } else if (action === 'generate_checklist') {
      setMessages(prev => [...prev, { role: 'user', text: 'Yes, generate my checklist!' }]);
      setSending(true);
      try {
        const res = await api.post('/ai/chat', {
          message: 'generate_checklist', language, event_type: type,
          event_context: buildEventContext()
        });
        setMessages(prev => [...prev, { role: 'gigi', text: res.data.response, quickChips: res.data.quickChips || null, outreachEvents: res.data.outreach_events || null }]);
        if (res.data.autoActions && onAction) {
          await onAction('add_checklist_batch', res.data.autoActions.map(a => a.data));
          onAction('show_toast', { message: `GiGi set up ${res.data.autoActions.length} checklist items for you!`, tab: 'checklist' });
        }
      } catch {
        setMessages(prev => [...prev, { role: 'gigi', text: "I couldn't generate the checklist. Please try again!" }]);
      }
      setSending(false);
    } else if (action === 'suggest_timeline') {
      setMessages(prev => [...prev, { role: 'user', text: 'Yes, suggest a day programme!' }]);
      setSending(true);
      try {
        const res = await api.post('/ai/chat', {
          message: 'suggest_timeline', language, event_type: type,
          event_context: buildEventContext()
        });
        setMessages(prev => [...prev, { role: 'gigi', text: res.data.response, quickChips: res.data.quickChips || null, outreachEvents: res.data.outreach_events || null }]);
        if (res.data.autoActions && onAction) {
          await onAction('add_timeline_batch', res.data.autoActions.map(a => a.data));
          onAction('show_toast', { message: `GiGi set up your day programme with ${res.data.autoActions.length} time slots!`, tab: 'timeline' });
        }
      } catch {
        setMessages(prev => [...prev, { role: 'gigi', text: "I couldn't suggest a timeline right now!" }]);
      }
      setSending(false);
    } else if (action === 'generate_thankyou') {
      setMessages(prev => [...prev, { role: 'user', text: 'Write a thank you message for my guests' }]);
      setSending(true);
      try {
        const res = await api.post('/ai/chat', {
          message: 'generate_thankyou', language, event_type: type,
          event_context: buildEventContext({
            hostName: context?.hostName || event?.host_name,
            invitationTheme: context?.invitationTheme,
            invitationColors: context?.invitationColors,
          })
        });
        setMessages(prev => [...prev, { role: 'gigi', text: res.data.response, quickChips: res.data.quickChips || null, outreachEvents: res.data.outreach_events || null }]);
        if (res.data.fillData && onAction) onAction(res.data.fillType, res.data.fillData);
      } catch {
        setMessages(prev => [...prev, { role: 'gigi', text: isSomber ? "I'm here to help. Let me try again." : "I couldn't write that right now. Please try again!" }]);
      }
      setSending(false);
    } else {
      setSending(true);
      try {
        const res = await api.post('/ai/chat', {
          message: action, language, event_type: type,
          event_context: buildEventContext()
        });
        setMessages(prev => [...prev, { role: 'gigi', text: res.data.response, quickChips: res.data.quickChips || null, outreachEvents: res.data.outreach_events || null }]);
      } catch {
        setMessages(prev => [...prev, { role: 'gigi', text: isSomber ? "I'm here for you. Let me try again in a moment." : "Oops! Let me try that again." }]);
      }
      setSending(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() && !uploadedFile) return;
    if (sending) return;
    const msg = input;
    setInput('');

    const msgObj = { role: 'user', text: msg };
    if (uploadPreview) {
      msgObj.image = uploadPreview;
    }
    if (uploadedFile && !uploadedFile.type.startsWith('image/')) {
      msgObj.fileName = uploadedFile.name;
    }
    setMessages(prev => [...prev, msgObj]);

    const parsed = parseEventFromMessage(msg);
    if (parsed && parsed.event_type) {
      setParsedEventCard(parsed);
      setEditableParsedEvent({ ...parsed });
    }

    setSending(true);
    try {
      let res;
      if (uploadedFile) {
        const formData = new FormData();
        formData.append('message', msg);
        formData.append('language', language);
        formData.append('file', uploadedFile);
        if (mode === 'vendor') {
          formData.append('event_type', 'vendor_assistant');
          formData.append('event_context', JSON.stringify(buildVendorContext()));
        } else {
          formData.append('event_type', event?.event_type || '');
          formData.append('event_context', JSON.stringify(buildEventContext()));
        }
        res = await api.post('/ai/chat', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        clearUpload();
      } else {
        const payload = mode === 'vendor'
          ? { message: msg, language, event_type: 'vendor_assistant', event_context: buildVendorContext() }
          : { message: msg, language, event_type: event?.event_type, event_context: buildEventContext() };
        res = await api.post('/ai/chat', payload);
      }
      setMessages(prev => [...prev, { role: 'gigi', text: res.data.response, quickChips: res.data.quickChips || null, outreachEvents: res.data.outreach_events || null }]);
      if (res.data.fillData && onAction) onAction(res.data.fillType, res.data.fillData);
      if (res.data.autoActions && onAction) {
        const actionType = res.data.autoActions[0]?.type;
        const items = res.data.autoActions.map(a => a.data);
        if (actionType === 'add_budget') {
          await onAction('add_budget_batch', items);
          onAction('show_toast', { message: `GiGi set up ${items.length} budget categories for you! Check your Budget tab.`, tab: 'budget' });
        } else if (actionType === 'add_checklist') {
          await onAction('add_checklist_batch', items);
          onAction('show_toast', { message: `GiGi set up ${items.length} checklist items for you!`, tab: 'checklist' });
        } else if (actionType === 'add_timeline') {
          await onAction('add_timeline_batch', items);
          onAction('show_toast', { message: `GiGi set up your day programme with ${items.length} time slots!`, tab: 'timeline' });
        } else if (actionType === 'remove_timeline') {
          await onAction('remove_timeline', res.data.autoActions[0].data);
          onAction('show_toast', { message: 'GiGi updated your programme!', tab: 'timeline' });
        } else if (actionType === 'update_timeline') {
          await onAction('update_timeline', res.data.autoActions[0].data);
          onAction('show_toast', { message: 'GiGi updated your programme!', tab: 'timeline' });
        } else if (actionType === 'reload_event') {
          if (onAction) onAction('reload_event');
        } else {
          for (const a of res.data.autoActions) { await onAction(a.type, a.data); }
        }
      }
    } catch {
      setMessages(prev => [...prev, { role: 'gigi', text: "I'm having a moment! Could you try asking that again?" }]);
    }
    setSending(false);
  };

  const sendMessageDirect = useCallback(async (msg) => {
    setSending(true);
    try {
      const payload = mode === 'vendor'
        ? { message: msg, language, event_type: 'vendor_assistant', event_context: buildVendorContext() }
        : { message: msg, language, event_type: event?.event_type, event_context: buildEventContext() };
      const res = await api.post('/ai/chat', payload);
      setMessages(prev => [...prev, { role: 'gigi', text: res.data.response, quickChips: res.data.quickChips || null, outreachEvents: res.data.outreach_events || null }]);
    } catch {
      setMessages(prev => [...prev, { role: 'gigi', text: "Let me try that again in a moment!" }]);
    }
    setSending(false);
  // eslint-disable-next-line
  }, [mode, vendorContext, event, context, language, sessionId]);

  const quickChips = mode === 'vendor' ? VENDOR_QUICK_CHIPS : (event ? [
    { type: 'navigate_tab', label: 'Find Vendors', tab: 'vendors' },
    { type: 'navigate_tab', label: 'Open Gift Registry', tab: 'registry' },
    { type: 'navigate_tab', label: 'Guest List & RSVPs', tab: 'guests' },
    { label: 'Tips for my event', msg: `Give me tips for planning a ${event.event_type}` },
    { label: 'Inspire me', msg: `Give me creative ideas and inspiration for my ${event.event_type}` },
  ] : []);

  // Handle quick-click chip actions from GiGi (vendors, navigation, actions)
  const handleQuickChipClick = (chip) => {
    console.log('[GiGi quick-chip] Clicked:', chip.type, chip);
    if (chip.type === 'view_vendor') {
      console.log('[GiGi quick-chip] Dispatching open_vendor_chat:', chip.vendor_name);
      window.dispatchEvent(new CustomEvent('gigi-action', {
        detail: { action: 'open_vendor_chat', vendorName: chip.vendor_name, vendorCategory: chip.vendor_category }
      }));
      setMessages(prev => [...prev, { role: 'gigi', text: `Opening ${chip.vendor_name}'s profile for you...` }]);
    } else if (chip.type === 'browse_category') {
      console.log('[GiGi quick-chip] Dispatching navigate to vendors with filter:', chip.category);
      window.dispatchEvent(new CustomEvent('gigi-action', {
        detail: { action: 'navigate', tab: 'vendors', filter: chip.category }
      }));
    } else if (chip.type === 'open_marketplace') {
      console.log('[GiGi quick-chip] Dispatching navigate to vendors (marketplace)');
      window.dispatchEvent(new CustomEvent('gigi-action', {
        detail: { action: 'navigate', tab: 'vendors' }
      }));
    } else if (chip.type === 'navigate_tab') {
      // Navigate to a specific tab in EventWorkspace
      window.dispatchEvent(new CustomEvent('gigi-action', {
        detail: { action: 'navigate', tab: chip.tab }
      }));
      setMessages(prev => [...prev, { role: 'gigi', text: `Taking you to ${chip.tab}...` }]);
    } else if (chip.type === 'navigate_url') {
      // Navigate to an absolute URL (e.g. sign-in pages)
      console.log('[GiGi quick-chip] Navigating to URL:', chip.url);
      if (chip.url) {
        window.location.href = chip.url;
      }
    } else if (chip.type === 'run_action') {
      // Trigger a specific action (generate_checklist, suggest_budget, etc.)
      if (chip.action === 'check_rsvps') {
        setMessages(prev => [...prev, { role: 'user', text: 'Who has RSVPed to my event?' }]);
        sendMessageDirect('Who has RSVPed to my event?');
      } else if (chip.action === 'ticket_pricing_help') {
        setMessages(prev => [...prev, { role: 'user', text: 'What ticket tiers should I create for my event?' }]);
        sendMessageDirect('What ticket tiers should I create for my event?');
      } else if (chip.action === 'generate_description') {
        handleAction('generate_description');
      } else if (chip.action === 'generate_checklist') {
        handleAction('generate_checklist');
      } else if (chip.action === 'suggest_budget_categories') {
        handleAction('suggest_budget_categories');
      } else if (chip.action === 'suggest_timeline') {
        handleAction('suggest_timeline');
      } else if (chip.action === 'generate_thankyou') {
        handleAction('generate_thankyou');
      } else {
        // Generic action — send as message (use msg if provided, otherwise label)
        const text = chip.msg || chip.label;
        setMessages(prev => [...prev, { role: 'user', text }]);
        sendMessageDirect(text);
      }
    }
    if (onAction) onAction('gigi_quick_chip', chip);
  };

  // Allow external components to open GiGi with a message
  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail?.message;
      if (msg) {
        setOpen(true);
        setExpanded(true);
        setUserDismissed(false);
        setNotifCount(0);
        const pageKey = context?.tab || context?.page || 'global';
        sessionStorage.removeItem('gigi_dismissed_' + pageKey);
        setMessages(prev => [...prev, { role: 'user', text: msg }]);
        sendMessageDirect(msg);
      }
    };
    window.addEventListener('gigi-ask', handler);
    return () => window.removeEventListener('gigi-ask', handler);
  }, [sendMessageDirect, context?.tab, context?.page]);

  // Handle external open triggers (e.g. Feedback tab in bottom nav)
  useEffect(() => {
    const handler = () => { if (!userDismissedRef.current) setOpen(prev => !prev); };
    window.addEventListener('gigi-open', handler);
    return () => window.removeEventListener('gigi-open', handler);
  }, []);

  const smartPrompts = getSmartPrompts(mode, context, event);

  const name = userName ? userName.split(' ')[0] : '';

  const isPanel = displayMode === 'panel';

  // Handle create event from parsed card
  const handleCreateParsedEvent = () => {
    const data = editingParsedEvent ? editableParsedEvent : parsedEventCard;
    if (onAction) onAction('create_event', data);
    setParsedEventCard(null);
    setEditingParsedEvent(false);
    setEditableParsedEvent(null);
  };

  const handleEditParsedEvent = () => {
    setEditingParsedEvent(true);
    setEditableParsedEvent({ ...(parsedEventCard || {}) });
  };

  const handleSmartPromptClick = (prompt) => {
    if (prompt.msg.startsWith('generate_') || prompt.msg.startsWith('suggest_')) {
      handleAction(prompt.msg);
    } else {
      setInput('');
      setMessages(prev => [...prev, { role: 'user', text: prompt.msg }]);
      sendMessageDirect(prompt.msg);
    }
  };

  // Panel sizing — always full size
  const bottomOffset = isMobile ? 88 : 24;
  const panelStyle = {
    position: 'fixed',
    bottom: `${bottomOffset}px`,
    right: '24px',
    zIndex: 9998,
    width: isMobile ? 'calc(100vw - 32px)' : '400px',
    maxWidth: 'calc(100vw - 48px)',
    height: isMobile ? '380px' : '500px',
    maxHeight: `calc(100vh - ${bottomOffset + 16}px)`,
    background: C.charcoal,
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 16px 48px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'gigiSlideIn 0.25s ease-out',
  };

  return (
    <>
      {/* ─── Proactive bubble ─── */}
      {bubble && !open && (
        <div style={{
          position: 'fixed', bottom: isMobile ? '164px' : '100px', right: '24px', zIndex: 9998,
          maxWidth: '300px', background: C.charcoal, borderRadius: '16px 16px 4px 16px',
          padding: '16px', boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
          border: `1px solid rgba(227,66,92,0.15)`, cursor: 'pointer',
          animation: 'gigiSlideIn 0.4s ease-out'
        }} onClick={handleBubbleClick}>
          <button onClick={e => { e.stopPropagation(); dismissBubble(); }} style={{
            position: 'absolute', top: '6px', right: '8px', background: 'none', border: 'none',
            cursor: 'pointer', color: 'rgba(255,255,255,0.4)', fontSize: '14px', padding: '2px'
          }}><FiX size={12} /></button>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <div style={{
              width: '32px', height: '32px', borderRadius: '10px',
              background: 'linear-gradient(135deg, rgba(227,66,92,0.2), rgba(227,66,92,0.05))',
              border: '1.5px solid rgba(227,66,92,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', flexShrink: 0
            }}>
              <img src={GIGI_AVATAR} alt="GiGi" style={{ width: '26px', height: '26px', objectFit: 'cover', objectPosition: 'center top' }} />
            </div>
            <p style={{ fontSize: '13px', lineHeight: 1.5, color: 'rgba(255,255,255,0.85)', margin: 0 }}>
              {bubble.text}
            </p>
          </div>
          <div style={{ marginTop: '10px', textAlign: 'right' }}>
            <span style={{ fontSize: '11px', color: C.coral, fontWeight: 600 }}>Tap to chat with GiGi</span>
          </div>
        </div>
      )}

      {/* ─── Floating GiGi button ─── */}
      <button onClick={handleOpen} style={{
        position: 'fixed', bottom: isMobile ? '88px' : '24px', right: '24px', zIndex: 9999,
        width: '60px', height: '60px', borderRadius: '50%',
        border: 'none',
        background: 'transparent',
        boxShadow: 'none',
        cursor: open ? 'default' : 'pointer',
        display: open ? 'none' : 'flex',
        alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.3s ease',
        overflow: 'visible',
        padding: 0,
        animation: sending ? 'gigiAvatarPulse 2s ease-in-out infinite' : 'none',
      }}
      onMouseEnter={e => { if (!open) { e.currentTarget.style.transform = 'scale(1.08)'; } }}
      onMouseLeave={e => { if (!open) { e.currentTarget.style.transform = 'scale(1)'; } }}
      >
        {/* GiGi unread badge */}
        {gigiBadge > 0 && <span style={{ position:'absolute',top:'0',right:'0',minWidth:'20px',height:'20px',borderRadius:'10px',background:'#E3425C',color:'#fff',fontSize:'11px',fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 5px',zIndex:10,fontFamily:'Inter,sans-serif' }}>{gigiBadge>9?'9+':gigiBadge}</span>}
        <img src={GIGI_AVATAR} alt="GiGi" style={{
          width: '44px', height: '44px',
          objectFit: 'cover', objectPosition: 'center top',
          borderRadius: '50%',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
          animation: sending ? 'none' : 'none',
        }} />
        {/* Online indicator */}
        <span style={{
          position: 'absolute', bottom: '2px', right: '2px',
          width: '12px', height: '12px',
          background: 'var(--green)', borderRadius: '50%',
          border: '2px solid var(--dark)'
        }} />
      </button>

      {/* ─── Chat panel ─── */}
      {open && (
        <div style={panelStyle}>
          {/* Header — slim, compact */}
          <div style={{
            padding: '6px 10px 6px 10px',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            display: 'flex', alignItems: 'center', gap: '8px',
            flexShrink: 0,
          }}>
            {/* GiGi image — small, no circle container */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <img src={GIGI_AVATAR} alt="GiGi" style={{
                width: '28px', height: '28px',
                objectFit: 'cover', objectPosition: 'center top',
                borderRadius: '50%',
              }} />
              {/* Tiny green pulsing online dot */}
              <span style={{
                position: 'absolute', bottom: '-1px', right: '-1px',
                width: '8px', height: '8px',
                background: '#10B981', borderRadius: '50%',
                border: '1.5px solid #1C1C1E',
                animation: 'gigiOnlinePulse 2s ease-in-out infinite',
              }} />
            </div>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <span style={{ color: 'white', fontWeight: 600, fontSize: '13px', lineHeight: 1.2 }}>GiGi</span>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', lineHeight: 1.3 }}>
                {mode === 'vendor' ? (
                  <><span style={{ display: 'block' }}>Your Business Growth</span><span style={{ display: 'block' }}>Assistant</span></>
                ) : (
                  <><span style={{ display: 'block' }}>Your Event Planner</span><span style={{ display: 'block' }}>Assistant</span></>
                )}
              </div>
            </div>
            {/* Language selector */}
            <div ref={langDropdownRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '3px' }}>
              <span style={{
                fontSize: '10px', fontWeight: 500, color: C.coral,
                background: 'rgba(227,66,92,0.12)', padding: '2px 5px',
                borderRadius: '5px',
              }}>
                {currentLang.label}
              </span>
              <button onClick={() => setLangDropdownOpen(!langDropdownOpen)} style={{
                background: 'none', border: 'none',
                borderRadius: '8px', width: '24px', height: '24px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'rgba(255,255,255,0.45)', transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = 'white'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; }}
              title={`Language: ${currentLang.label}`}
              >
                <FiGlobe size={13} />
              </button>
              {langDropdownOpen && (
                <div style={{
                  position: 'absolute', top: '34px', right: 0, zIndex: 10000,
                  background: '#2C2C2E', borderRadius: '12px',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  minWidth: '160px', overflow: 'hidden',
                  animation: 'gigiSlideIn 0.2s ease-out',
                }}>
                  {LANGUAGES.map(lang => (
                    <button key={lang.code} onClick={() => handleLanguageChange(lang.code)} style={{
                      width: '100%', padding: '10px 16px', border: 'none',
                      background: language === lang.code ? 'rgba(227,66,92,0.15)' : 'transparent',
                      color: language === lang.code ? C.coral : 'rgba(255,255,255,0.8)',
                      fontSize: '13px', fontWeight: language === lang.code ? 600 : 400,
                      cursor: 'pointer', textAlign: 'left',
                      display: 'flex', alignItems: 'center', gap: '8px',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { if (language !== lang.code) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                    onMouseLeave={e => { if (language !== lang.code) e.currentTarget.style.background = 'transparent'; }}
                    >
                      {language === lang.code && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: C.coral }} />}
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Close button — placed neatly at top-right, borderless */}
            <button onClick={handleClose} style={{
              background: 'none', border: 'none',
              borderRadius: '8px', width: '28px', height: '28px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: 'rgba(255,255,255,0.45)', transition: 'all 0.2s'
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'white'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; }}
            ><FiX size={15} /></button>
          </div>

          {/* Chat history panel */}
          {showHistory && (
            <div style={{
              padding: '12px', background: 'rgba(0,0,0,0.25)',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              maxHeight: '200px', overflowY: 'auto',
            }}>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                Previous Conversations
              </div>
              {chatSessions.map((s, i) => (
                <div key={s.session_id || i} onClick={() => handleLoadSession(s.session_id)}
                  style={{
                    padding: '10px 12px', borderRadius: '10px', marginBottom: '4px',
                    background: s.session_id === sessionId ? 'rgba(227,66,92,0.12)' : 'rgba(255,255,255,0.04)',
                    border: s.session_id === sessionId ? '1px solid rgba(227,66,92,0.2)' : '1px solid rgba(255,255,255,0.04)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (s.session_id !== sessionId) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                  onMouseLeave={e => { if (s.session_id !== sessionId) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                >
                  <FiMessageSquare size={13} style={{ color: s.session_id === sessionId ? C.coral : 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {s.first_message || 'Chat session'}
                    </div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' }}>
                      {new Date(s.last_message_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      {' · '}{s.message_count} messages
                    </div>
                  </div>
                  <button onClick={(e) => handleDeleteSession(s.session_id, e)} title="Delete chat"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'rgba(255,255,255,0.2)', padding: '4px', borderRadius: '6px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.2)'; e.currentTarget.style.background = 'none'; }}
                  >
                    <FiTrash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '16px',
            display: 'flex', flexDirection: 'column', gap: '12px',
            background: 'rgba(0,0,0,0.15)',
            scrollBehavior: 'smooth',
          }}>
            {/* Context reminder banner — shows when user returns to a previous conversation */}
            {contextReminder && messages.length === 0 && !hasGreeted && (
              <div style={{
                margin: '0 0 8px', padding: '12px 14px',
                background: 'rgba(227,66,92,0.08)', border: '1px solid rgba(227,66,92,0.15)',
                borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '10px',
              }}>
                <FiClock size={14} style={{ color: C.coral, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>Previous conversation</div>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {contextReminder.lastMessage || 'You were chatting with GiGi'}
                  </div>
                </div>
                <button onClick={() => { handleLoadSession(contextReminder.sessionId); setContextReminder(null); }}
                  style={{ padding: '6px 12px', borderRadius: '8px', background: C.coral, color: 'white', border: 'none', fontSize: '11px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  Continue
                </button>
                <button onClick={() => setContextReminder(null)}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', padding: '2px' }}>
                  <FiX size={12} />
                </button>
              </div>
            )}
            {/* Initial quick chips — shown before any messages are exchanged */}
            {messages.length === 0 && quickChips.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(227,66,92,0.15), rgba(227,66,92,0.05))',
                  border: '1px solid rgba(227,66,92,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden', flexShrink: 0,
                }}>
                  <img src={GIGI_AVATAR} alt="" style={{ width: '22px', height: '22px', objectFit: 'cover', objectPosition: 'center top', borderRadius: '50%' }} />
                </div>
                <div style={{ maxWidth: '80%' }}>
                  <div style={{
                    padding: '12px 16px', borderRadius: '16px 16px 16px 4px',
                    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.06)',
                    color: 'rgba(255,255,255,0.9)', fontSize: '13.5px', lineHeight: 1.6,
                  }}>
                    {mode === 'vendor'
                      ? "Hi! I'm GiGi, your vendor assistant. Here are some things I can help with:"
                      : "Hi! I'm GiGi, your AI event assistant. What would you like to do?"}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                    {quickChips.map((chip, ci) => {
                      const isVendor = chip.type === 'view_vendor';
                      const isNavigate = chip.type === 'navigate_tab';
                      const isBrowse = chip.type === 'browse_category' || chip.type === 'open_marketplace';
                      const bg = isVendor || isBrowse ? 'rgba(16,185,129,0.15)' :
                                 isNavigate ? 'rgba(59,130,246,0.12)' : 'rgba(227,66,92,0.12)';
                      const borderColor = isVendor || isBrowse ? 'rgba(16,185,129,0.25)' :
                                          isNavigate ? 'rgba(59,130,246,0.22)' : 'rgba(227,66,92,0.2)';
                      const textColor = isVendor || isBrowse ? '#10B981' :
                                        isNavigate ? '#60A5FA' : C.coral;
                      return (
                        <button key={ci} onClick={() => handleQuickChipClick(chip)}
                          style={{
                            padding: '7px 14px', borderRadius: '20px',
                            background: bg, border: `1px solid ${borderColor}`,
                            color: textColor, fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '5px',
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.2)'; }}
                          onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; }}
                        >
                          {chip.type === 'navigate_tab' ? <FiChevronDown size={12} style={{transform: 'rotate(-90deg)'}} /> :
                           chip.type === 'view_vendor' ? <FiUser size={12} /> :
                           chip.type === 'browse_category' ? <FiSearch size={12} /> :
                           chip.type === 'open_marketplace' ? <FiGrid size={12} /> :
                           <FiMessageSquare size={12} />}
                          {chip.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{
                display: 'flex',
                flexDirection: m.role === 'user' ? 'row-reverse' : 'row',
                alignItems: 'flex-end', gap: '8px'
              }}>
                {m.role === 'gigi' && (
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(227,66,92,0.15), rgba(227,66,92,0.05))',
                    border: '1px solid rgba(227,66,92,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', flexShrink: 0,
                    animation: sending && i === messages.length - 1 ? 'gigiAvatarPulse 1.5s ease-in-out infinite' : 'none',
                  }}>
                    <img src={GIGI_AVATAR} alt="" style={{ width: '22px', height: '22px', objectFit: 'cover', objectPosition: 'center top', borderRadius: '50%' }} />
                  </div>
                )}
                <div style={{ maxWidth: '80%' }}>
                  {m.image && (
                    <div style={{
                      marginBottom: '6px',
                      borderRadius: m.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                      overflow: 'hidden',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}>
                      <img src={m.image} alt="Upload" style={{
                        maxWidth: '200px', maxHeight: '150px', display: 'block',
                        objectFit: 'cover',
                      }} />
                    </div>
                  )}
                  {m.fileName && (
                    <div style={{
                      padding: '8px 12px', marginBottom: '4px',
                      borderRadius: '8px', background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      fontSize: '12px', color: 'rgba(255,255,255,0.6)',
                      display: 'flex', alignItems: 'center', gap: '6px',
                    }}>
                      <FiUpload size={12} /> {m.fileName}
                    </div>
                  )}
                  <div style={{
                    padding: '12px 16px',
                    borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    background: m.role === 'user'
                      ? `rgba(227,66,92,0.2)`
                      : 'rgba(255,255,255,0.08)',
                    color: m.role === 'user' ? 'white' : 'rgba(255,255,255,0.9)',
                    border: m.role === 'user'
                      ? '1px solid rgba(227,66,92,0.15)'
                      : '1px solid rgba(255,255,255,0.06)',
                    fontSize: '13.5px', lineHeight: 1.6,
                    whiteSpace: 'pre-wrap'
                  }}>
                    {m.text}
                  </div>
                  {/* Quick-click action chips from GiGi */}
                  {m.quickChips && m.quickChips.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                      {m.quickChips.map((chip, ci) => {
                        const isVendor = chip.type === 'view_vendor';
                        const isNavigate = chip.type === 'navigate_tab';
                        const isAction = chip.type === 'run_action';
                        const isBrowse = chip.type === 'browse_category' || chip.type === 'open_marketplace';
                        const bg = isVendor || isBrowse ? 'rgba(16,185,129,0.15)' :
                                   isNavigate ? 'rgba(59,130,246,0.12)' :
                                   isAction ? 'rgba(227,66,92,0.12)' : 'rgba(227,66,92,0.12)';
                        const borderColor = isVendor || isBrowse ? 'rgba(16,185,129,0.25)' :
                                            isNavigate ? 'rgba(59,130,246,0.22)' :
                                            isAction ? 'rgba(227,66,92,0.2)' : 'rgba(227,66,92,0.2)';
                        const textColor = isVendor || isBrowse ? '#10B981' :
                                          isNavigate ? '#60A5FA' :
                                          isAction ? C.coral : C.coral;
                        return (
                          <button key={ci} onClick={() => handleQuickChipClick(chip)}
                            style={{
                              padding: '7px 14px', borderRadius: '20px',
                              background: bg, border: `1px solid ${borderColor}`,
                              color: textColor, fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: '5px',
                              transition: 'all 0.15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.2)'; }}
                            onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; }}
                          >
                            {chip.type === 'view_vendor' ? <FiUser size={12} /> :
                             chip.type === 'browse_category' ? <FiSearch size={12} /> :
                             chip.type === 'open_marketplace' ? <FiGrid size={12} /> :
                             chip.type === 'navigate_tab' ? <FiChevronDown size={12} style={{transform: 'rotate(-90deg)'}} /> :
                             <FiMessageSquare size={12} />}
                            {chip.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {/* Vendor outreach event cards */}
                  {mode === 'vendor' && m.outreachEvents && m.outreachEvents.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Matching Events
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {m.outreachEvents.map((ev, ei) => (
                          <div key={ei} style={{
                            background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)',
                            borderRadius: '10px', padding: '10px 12px',
                            display: 'flex', flexDirection: 'column', gap: '6px',
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ color: 'white', fontWeight: 700, fontSize: '13px' }}>{ev.title}</span>
                              <span style={{
                                background: 'rgba(16,185,129,0.15)', color: '#10B981',
                                padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 600,
                              }}>{ev.event_type}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: 'rgba(255,255,255,0.5)', flexWrap: 'wrap' }}>
                              {ev.event_date && <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><FiCalendar size={10} /> {ev.event_date}</span>}
                              {ev.event_location && <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><FiMapPin size={10} /> {ev.event_location}</span>}
                              {ev.estimated_guests && <span>{ev.estimated_guests} guests</span>}
                            </div>
                            <button onClick={() => { if (onAction) onAction('vendor_outreach_event', ev); }}
                              style={{
                                background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.2)',
                                color: '#10B981', padding: '6px 12px', borderRadius: '6px',
                                fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '5px',
                                alignSelf: 'flex-start',
                              }}>
                              <FiSend size={11} /> Send Rate Card
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Parsed event card */}
            {parsedEventCard && (
              <div style={{
                background: 'rgba(227,66,92,0.08)', border: '1px solid rgba(227,66,92,0.2)',
                borderRadius: '14px', padding: '14px 16px', marginTop: '4px',
              }}>
                <div style={{ color: C.coral, fontWeight: 700, fontSize: '13px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FiPlus size={14} /> Event Detected
                </div>
                {editingParsedEvent ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {editableParsedEvent?.event_type !== undefined && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', minWidth: '70px' }}>Type:</span>
                        <input value={editableParsedEvent.event_type || ''} onChange={e => setEditableParsedEvent(prev => ({ ...prev, event_type: e.target.value }))} style={{
                          flex: 1, padding: '6px 10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)',
                          background: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '12px', outline: 'none',
                        }} />
                      </div>
                    )}
                    {editableParsedEvent?.estimated_guests !== undefined && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', minWidth: '70px' }}>Guests:</span>
                        <input type="number" value={editableParsedEvent.estimated_guests || ''} onChange={e => setEditableParsedEvent(prev => ({ ...prev, estimated_guests: parseInt(e.target.value, 10) || 0 }))} style={{
                          flex: 1, padding: '6px 10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)',
                          background: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '12px', outline: 'none',
                        }} />
                      </div>
                    )}
                    {editableParsedEvent?.budget !== undefined && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', minWidth: '70px' }}>Budget:</span>
                        <input type="number" value={editableParsedEvent.budget || ''} onChange={e => setEditableParsedEvent(prev => ({ ...prev, budget: parseFloat(e.target.value) || 0 }))} style={{
                          flex: 1, padding: '6px 10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)',
                          background: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '12px', outline: 'none',
                        }} />
                      </div>
                    )}
                    {editableParsedEvent?.location !== undefined && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', minWidth: '70px' }}>Location:</span>
                        <input value={editableParsedEvent.location || ''} onChange={e => setEditableParsedEvent(prev => ({ ...prev, location: e.target.value }))} style={{
                          flex: 1, padding: '6px 10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)',
                          background: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '12px', outline: 'none',
                        }} />
                      </div>
                    )}
                    {editableParsedEvent?.event_date !== undefined && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', minWidth: '70px' }}>Date:</span>
                        <input value={editableParsedEvent.event_date || ''} onChange={e => setEditableParsedEvent(prev => ({ ...prev, event_date: e.target.value }))} style={{
                          flex: 1, padding: '6px 10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)',
                          background: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '12px', outline: 'none',
                        }} />
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {parsedEventCard.event_type && (
                      <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>Type: </span>{parsedEventCard.event_type}
                      </div>
                    )}
                    {parsedEventCard.estimated_guests && (
                      <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>Guests: </span>{parsedEventCard.estimated_guests}
                      </div>
                    )}
                    {parsedEventCard.budget && (
                      <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>Budget: </span>R{parsedEventCard.budget.toLocaleString()}
                      </div>
                    )}
                    {parsedEventCard.location && (
                      <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>Location: </span>{parsedEventCard.location}
                      </div>
                    )}
                    {parsedEventCard.event_date && (
                      <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>Date: </span>{parsedEventCard.event_date}
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                  <button onClick={handleCreateParsedEvent} style={{
                    flex: 1, padding: '8px 14px', borderRadius: '8px',
                    background: C.coral, color: 'white', border: 'none',
                    fontWeight: 600, fontSize: '12px', cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    Create this event
                  </button>
                  {!editingParsedEvent && (
                    <button onClick={handleEditParsedEvent} style={{
                      padding: '8px 14px', borderRadius: '8px',
                      background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      fontWeight: 500, fontSize: '12px', cursor: 'pointer',
                      transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '4px',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                    >
                      <FiEdit2 size={11} /> Edit details
                    </button>
                  )}
                  <button onClick={() => { setParsedEventCard(null); setEditingParsedEvent(false); setEditableParsedEvent(null); }} style={{
                    padding: '8px 10px', borderRadius: '8px',
                    background: 'transparent', color: 'rgba(255,255,255,0.3)',
                    border: 'none', cursor: 'pointer', fontSize: '12px',
                  }}>
                    <FiX size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* Sending indicator */}
            {sending && (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(227,66,92,0.15), rgba(227,66,92,0.05))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden',
                  animation: 'gigiAvatarPulse 1.5s ease-in-out infinite',
                }}>
                  <img src={GIGI_AVATAR} alt="" style={{ width: '22px', height: '22px', objectFit: 'cover', objectPosition: 'center top', borderRadius: '50%' }} />
                </div>
                <div style={{ padding: '14px 20px', borderRadius: '16px 16px 16px 4px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: C.coral, animation: 'gigiBounce 0.6s ease-in-out infinite', animationDelay: '0s' }} />
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: C.coral, animation: 'gigiBounce 0.6s ease-in-out infinite', animationDelay: '0.15s' }} />
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: C.coral, animation: 'gigiBounce 0.6s ease-in-out infinite', animationDelay: '0.3s' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Upload preview */}
          {uploadedFile && (
            <div style={{
              padding: '8px 16px', borderTop: '1px solid rgba(255,255,255,0.06)',
              background: 'rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: '8px',
              flexShrink: 0,
            }}>
              {uploadPreview ? (
                <img src={uploadPreview} alt="Preview" style={{
                  width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover',
                  border: '1px solid rgba(255,255,255,0.1)',
                }} />
              ) : (
                <div style={{
                  width: '40px', height: '40px', borderRadius: '8px',
                  background: 'rgba(255,255,255,0.06)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}>
                  <FiUpload size={14} color="rgba(255,255,255,0.4)" />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {uploadedFile.name}
                </div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>
                  {(uploadedFile.size / 1024).toFixed(1)} KB
                </div>
              </div>
              <button onClick={clearUpload} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'rgba(255,255,255,0.4)', padding: '4px',
              }}>
                <FiX size={14} />
              </button>
            </div>
          )}

          {/* Input area — ChatGPT-style: [+] [input] [mic] [send] */}
          <div style={{
            padding: '10px 12px', borderTop: '1px solid rgba(255,255,255,0.06)',
            display: 'flex', gap: '6px', background: 'rgba(255,255,255,0.02)',
            alignItems: 'center', flexShrink: 0,
          }}>
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf,.doc,.docx"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {/* Plus / upload button — at the beginning like ChatGPT */}
            <button
              onClick={() => fileInputRef.current?.click()}
              title="Upload image or file"
              style={{
                width: '38px', height: '38px', borderRadius: '12px', border: 'none',
                background: uploadedFile ? 'rgba(227,66,92,0.15)' : 'rgba(255,255,255,0.06)',
                color: uploadedFile ? C.coral : 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s', flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = uploadedFile ? 'rgba(227,66,92,0.15)' : 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = uploadedFile ? C.coral : 'rgba(255,255,255,0.4)'; }}
            >
              <FiPlus size={17} />
            </button>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMessage()}
              placeholder="Ask GiGi anything..."
              style={{
                flex: 1, padding: '11px 14px', borderRadius: '14px',
                border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.05)',
                fontSize: '14px', outline: 'none',
                color: 'white', minWidth: 0,
              }}
            />
            {/* Mic button */}
            <button
              onClick={voiceSupported ? (isRecording ? stopRecording : startRecording) : undefined}
              title={voiceSupported ? (isRecording ? 'Stop recording' : 'Voice input') : 'Voice not supported in this browser'}
              style={{
                width: '38px', height: '38px', borderRadius: '12px', border: 'none',
                background: isRecording ? 'rgba(227,66,92,0.25)' : 'rgba(255,255,255,0.06)',
                color: isRecording ? C.coral : (voiceSupported ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.15)'),
                cursor: voiceSupported ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s', flexShrink: 0,
                animation: isRecording ? 'gigiMicPulse 1.5s ease-in-out infinite' : 'none',
                opacity: voiceSupported ? 1 : 0.5,
              }}
              onMouseEnter={e => { if (voiceSupported && !isRecording) { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; } }}
              onMouseLeave={e => { if (voiceSupported && !isRecording) { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; } }}
            >
              <FiMic size={15} />
            </button>
            {/* Send button */}
            <button
              onClick={sendMessage}
              disabled={(!input.trim() && !uploadedFile) || sending}
              style={{
                width: '40px', height: '40px', borderRadius: '13px', border: 'none',
                background: (input.trim() || uploadedFile) ? C.coral : 'rgba(255,255,255,0.06)',
                color: (input.trim() || uploadedFile) ? 'white' : 'rgba(255,255,255,0.2)',
                cursor: (input.trim() || uploadedFile) ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s', flexShrink: 0,
              }}
            >
              <FiSend size={16} />
            </button>
          </div>

          {/* Smart prompts row */}
          {smartPrompts.length > 0 && (
            <div style={{
              padding: '6px 12px 8px',
              display: 'flex', gap: '6px',
              overflowX: 'auto',
              borderTop: '1px solid rgba(255,255,255,0.04)',
              background: 'rgba(0,0,0,0.08)',
              flexShrink: 0,
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}>
              {smartPrompts.map((p, i) => (
                <button key={i} onClick={() => handleSmartPromptClick(p)} style={{
                  padding: '6px 14px', borderRadius: '20px', fontSize: '12px',
                  background: 'rgba(227,66,92,0.1)', border: '1px solid rgba(227,66,92,0.2)',
                  color: C.coral, cursor: 'pointer', fontWeight: 500,
                  transition: 'all 0.2s', whiteSpace: 'nowrap', flexShrink: 0,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(227,66,92,0.2)'; e.currentTarget.style.borderColor = 'rgba(227,66,92,0.35)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(227,66,92,0.1)'; e.currentTarget.style.borderColor = 'rgba(227,66,92,0.2)'; }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes gigiSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes gigiPanelSlideIn {
          from { opacity: 0; transform: translateX(100%); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes gigiFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes gigiBounce {
          0%, 100% { transform: translateY(0); opacity: 0.4; }
          50% { transform: translateY(-4px); opacity: 1; }
        }
        @keyframes gigiWave {
          0%, 100% { transform: rotate(0deg); }
          20% { transform: rotate(-14deg); }
          55% { transform: rotate(14deg); }
          80% { transform: rotate(-5deg); }
        }
        @keyframes gigiBadgePulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        @keyframes gigiAvatarGlow {
          0%, 100% { box-shadow: 0 6px 24px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05), 0 0 8px rgba(227,66,92,0.1); }
          50% { box-shadow: 0 6px 24px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06), 0 0 14px rgba(227,66,92,0.18); }
        }
        @keyframes gigiAvatarPulse {
          0%, 100% { box-shadow: 0 6px 24px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05), 0 0 8px rgba(227,66,92,0.2); border-color: rgba(227,66,92,0.2); }
          50% { box-shadow: 0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.08), 0 0 24px rgba(227,66,92,0.4); border-color: rgba(227,66,92,0.5); }
        }
        @keyframes gigiMicPulse {
          0%, 100% { background: rgba(227,66,92,0.15); box-shadow: 0 0 0 0 rgba(227,66,92,0.3); }
          50% { background: rgba(227,66,92,0.3); box-shadow: 0 0 12px 4px rgba(227,66,92,0.2); }
        }
        @keyframes gigiOnlinePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.7); }
        }
        @media (max-width: 767px) {
          /* On mobile, mini mode takes nearly full width */
        }
      `}</style>
    </>
  );
}
